/**
 * Typed client methods for the BEAM billing surface (WO-M4).
 *
 * Routes confirmed in beam-backend/src/api/billingRouter.ts + domainRouter.ts:
 *   GET  /api/estimates                            → EstimateSummary[]
 *   GET  /api/estimates/:id                        → EstimateDetail
 *   POST /api/estimates/:id/transition  { to }     → EstimateDetail (transition)
 *   GET  /api/invoices                             → InvoiceSummary[]
 *   GET  /api/invoices/:id                         → InvoiceDetail
 *   POST /api/invoices/:id/payment-link            → PaymentLinkResponse
 *
 * All shapes from @beam/api-types — never re-declared here.
 * get/post helpers attach the Bearer token automatically (src/api/client.ts).
 * Scope derived from JWT server-side; no PII in URLs (mobile CLAUDE.md invariant 2).
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { get, post } from './client';
import type { ApprovalStatus } from '@beam/api-types';
import {
  EstimateSummary,
  EstimateDetail,
  InvoiceSummary,
  InvoiceDetail,
  PaymentLinkResponse,
} from '@beam/api-types';

// ---------------------------------------------------------------------------
// Estimates
// ---------------------------------------------------------------------------

export interface ListEstimatesParams {
  status?: string;
  approval_status?: string;
}

/**
 * List estimate summaries for the authenticated org.
 * Optionally filter by status or approval_status.
 */
export async function listEstimates(
  params: ListEstimatesParams = {},
): Promise<EstimateSummary[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.approval_status) qs.set('approval_status', params.approval_status);
  const query = qs.toString();
  const raw = await get<unknown>(`/api/estimates${query ? '?' + query : ''}`);
  return (raw as unknown[]).map((item) => EstimateSummary.parse(item));
}

/**
 * Fetch the full detail (with line items) for a single estimate.
 * estimateId is an opaque UUID path param — no PII in URL.
 */
export async function getEstimate(estimateId: string): Promise<EstimateDetail> {
  const raw = await get<unknown>(`/api/estimates/${estimateId}`);
  return EstimateDetail.parse(raw);
}

/**
 * Transition an estimate's approval_status.
 * Server enforces role-based access: owner-only transitions (approved,
 * sent_for_signature) are rejected server-side for staff.
 *
 * @param estimateId  - opaque UUID (no PII in URL)
 * @param to          - target ApprovalStatus
 */
export async function transitionEstimate(
  estimateId: string,
  to: ApprovalStatus,
): Promise<EstimateDetail> {
  const raw = await post<unknown>(`/api/estimates/${estimateId}/transition`, { to });
  return EstimateDetail.parse(raw);
}

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export interface ListInvoicesParams {
  status?: string;
  customer_id?: string;
}

/**
 * List invoice summaries for the authenticated org.
 * Optionally filter by status or customer_id.
 * customer_id is an opaque UUID — not PII-identifying in the URL (CLAUDE.md §2).
 */
export async function listInvoices(
  params: ListInvoicesParams = {},
): Promise<InvoiceSummary[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.customer_id) qs.set('customer_id', params.customer_id);
  const query = qs.toString();
  const raw = await get<unknown>(`/api/invoices${query ? '?' + query : ''}`);
  return (raw as unknown[]).map((item) => InvoiceSummary.parse(item));
}

/**
 * Fetch full invoice detail (line items + payments) for a single invoice.
 * invoiceId is an opaque UUID — no PII in URL.
 */
export async function getInvoice(invoiceId: string): Promise<InvoiceDetail> {
  const raw = await get<unknown>(`/api/invoices/${invoiceId}`);
  return InvoiceDetail.parse(raw);
}

/**
 * Create or retrieve a Stripe hosted payment link for an invoice.
 *
 * Response status:
 *   'created'      → Stripe was configured; url is the hosted payment URL.
 *   'unconfigured' → STRIPE_SECRET_KEY absent on the server; no url is returned.
 *
 * The caller must handle both cases gracefully — never crash on 'unconfigured'.
 */
export async function createPaymentLink(
  invoiceId: string,
): Promise<PaymentLinkResponse> {
  const raw = await post<unknown>(`/api/invoices/${invoiceId}/payment-link`);
  return PaymentLinkResponse.parse(raw);
}
